import {
  ApplicationRef,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {IDsDropdownOption} from "./ds-dropdown-option";
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR, ReactiveFormsModule,
  ValidationErrors,
  Validator
} from "@angular/forms";
import {DsDropdownListComponent} from "./ds-dropdown-list/ds-dropdown-list.component";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {NgClass, NgForOf, NgIf, NgStyle} from "@angular/common";
import {DsTooltipDirective} from "../../directives/ds-tooltip/ds-tooltip.directive";

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'ds-dropdown',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './ds-dropdown.component.html',
    styleUrls: ['./ds-dropdown.component.scss'],
    imports: [
        NgStyle,
        NgClass,
        NgIf,
        DsTooltipDirective,
        ReactiveFormsModule,
        NgForOf
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DsDropdownComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DsDropdownComponent),
            multi: true,
        }
    ]
})

export class DsDropdownComponent implements OnInit, OnChanges, ControlValueAccessor, Validator, OnDestroy {

  @Input() public value?: IDsDropdownOption['value'];
  @Input() public dsDropdownSearch?: boolean = false;
  @Input() public dsDropdownWithArrowIcon?: boolean = true;
  @Input() public dsDropdownWithSearchIcon?: boolean = false;
  @Input() public dsDropdownOptions: IDsDropdownOption[];
  @Input() public dsDropdownLabel?: string;
  @Input() public imgPath?: string;
  @Input() public dsDropdownFullyHover?: boolean;
  @Input() public dsDropdownPlaceHolder?: string = '';
  @Input() public dsDropdownPosition?: 'top' | 'bottom' = 'bottom';
  @Input() public dsDropdownAlign?: 'left' | 'right' = 'right';
  @Input() public dsDropdownWidth?: string = '100%';
  @Input() public dsDropdownHeight?: string = '40px';
  @Input() public dsDropdownDisabled?: boolean = false;
  @Input() public dsDropdownPlaceholderStyle?: boolean = false;
  @Input() public dsDropdownSmallFont?: boolean = false;
  @Input() public dsDropdownListWidth?: number;
  @Input() public dsDropdownListMaxHeight?: string = '185px';
  @Input() public dsDropDownScrollingElement?: string;
  @Input() public dsDropdownOneLine?: boolean;
  @Input() public dsDropdownTooltip?: string = '';
  @Input() public dsDropdownSelectedInputHolder = false;
  @Input() public dsDropdownSimpleList = false;
  @Input() public showDropDown = true;
  @Input() public smallSize = false;
  @Input() public dsDropdownAddIfEmpty = false;
  @Input() public dsDropdownKeepInputValue = false;
  @Input() public darkMode = false;
  @Input() public selectOnEnter = false;
  @Input() public selectOnFocusOut = false;
  @Input() public overflowHidden = false;
  @Input() public changeAfterConfirmation = false;
  @Input() public customDropdown = false;
  @Input() public color?: string;
  @Input() public notValid?: string;
  @Input() keepPlaceholder?: boolean = false;
  @Input() customFontFamily?: boolean = false;

  @Output() public onOptionChoose = new EventEmitter<IDsDropdownOption['value'] | {
    value: IDsDropdownOption['value'],
    group: IDsDropdownOption['group']
  }>();
  @Output() public searchOutside = new EventEmitter<any['value']>();
  @Output() public onFullOptionChoose = new EventEmitter<IDsDropdownOption>();
  @Output() public onFilterChange = new EventEmitter<string>();

  @ViewChild('toggle') toggle: ElementRef;
  @ViewChild('templateRef') template: TemplateRef<any>;
  @ViewChild('searchInput') searchInput: ElementRef;
  selected = false;

  public selectedOption: IDsDropdownOption;
  public optionFilter: FormControl = new FormControl('');
  public filteredOptions: IDsDropdownOption[];
  public toggled: boolean = false;
  public inputFocused = false;
  public delayTimer: any;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  private _componentRef: ComponentRef<DsDropdownListComponent>;

  private readonly _click: Function;
  private _propagateChange: Function = (_: any) => {
  };

  constructor(
    private renderer2: Renderer2,
    private _applicationRef: ApplicationRef,
    private _injector: Injector,
    private _componentFactoryResolver: ComponentFactoryResolver,
    private _cdRef: ChangeDetectorRef,
  ) {
    this._click = this.renderer2.listen(document, "click", event => {
      if (!this.toggle?.nativeElement?.contains(event.target)) {
        this.hideDropdownList(event.target);
      } else {
        if (!this.dsDropdownSearch) {
          this.toggled ? this.hideDropdownList(event.target) : this.showDropdownList();
        } else {
          this.toggled ? event.stopImmediatePropagation() : this.showDropdownList();
        }
      }
    });
  }

  ngOnInit() {
    this.filteredOptions = this.dsDropdownOptions;
    this.optionFilter.valueChanges
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(value => {
        this.onFilterChange.emit(value);
        if (!this.showDropDown) {
          clearTimeout(this.delayTimer);
          this.delayTimer = setTimeout(() => {
            let el = document.querySelector('.ds-dropdown-list > div');
            el && (el.scrollTop = 0);
            this.searchOutside.emit(value);
          }, 125);
          return;
        }
        if (!value) {
          this.filteredOptions = this.dsDropdownOptions;
        } else {
          this.filteredOptions = this.dsDropdownOptions
            && this.dsDropdownOptions.filter(option => {
              const v = value.toLocaleLowerCase();
              return option.label.toLocaleLowerCase().includes(v)
                || (option.keywords && option.keywords.some(kw => kw.toLocaleLowerCase().includes(v)));
            });

          if (this.dsDropdownAddIfEmpty) {
            const containsFully = this.dsDropdownOptions
              && this.dsDropdownOptions.filter(option => {
                const v = value.toLocaleLowerCase();
                return option.label.toLocaleLowerCase() === v;
              });
            if (!containsFully.length) {
              this.filteredOptions.push({label: value, value: null});
            }
          }
          this.filteredOptions = this.filteredOptions && this.filteredOptions.length ? this.filteredOptions : this.dsDropdownOptions;
        }
        this._componentRef && (this._componentRef.instance.hide = !this.filteredOptions.length);
      });
    /*if (this.dsDropDownScrollingElement) {
      this.hideDropdownOnScroll();
    }*/
  }

  /*  private hideDropdownOnScroll(): void {
      fromEvent(document.getElementsByClassName(this.dsDropDownScrollingElement), 'scroll')
        .subscribe(() => this.hideDropdownList());
      fromEvent(document.getElementsByClassName('mobile'), 'scroll')
        .subscribe(() => this.hideDropdownList());
    }*/

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dsDropdownOptions'] && changes['dsDropdownOptions'].currentValue) {
      this.dsDropdownOptions = changes['dsDropdownOptions'].currentValue;
    }
    this.filteredOptions = this.dsDropdownOptions;
    if (changes['value']) {
      this.selectedOption = this.getOptionByValue(changes['value'].currentValue);
    }
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
    this.hideDropdownList();
    this._click();
  }

  private getOptionByValue(value: any): IDsDropdownOption {
    return this.dsDropdownOptions && this.dsDropdownOptions.find((option) => option.value === value);
  }

  writeValue(value: any): void {
    this.selectedOption = this.getOptionByValue(value);
  }

  registerOnChange(fn: any): void {
    this._propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
  }

  validate(c: AbstractControl): ValidationErrors {
    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
  }

  setDisabledState(isDisabled: boolean): void {
  }

  onEnter(event) {
    if (event.code === 'Enter' && this.filteredOptions?.length && this.selectOnEnter) {
      this.selectOption(this.filteredOptions[this.filteredOptions?.length - 1]);
      this.hideDropdownList();
      this.searchInput.nativeElement.blur();
    }
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (this.selected) {
        this.selected = false;
        return;
      }
      if (event.target.value && this.filteredOptions?.length && this.selectOnFocusOut) {
        this.selectOption(this.filteredOptions[this.filteredOptions?.length - 1]);
        this.hideDropdownList();
        this.searchInput.nativeElement.blur();
      }
    }, 500);
  }

  public selectOption(option): void {
    this.selected = true;
    if (!this.changeAfterConfirmation && !this.keepPlaceholder) {
      this.selectedOption = option;
    }
    this._propagateChange(option.value);
    if (option.group) {
      // Emit both value and group if group exists
      this.onOptionChoose.emit({value: option.value, group: option.group});
    } else {
      this.onOptionChoose.emit(option.value);
    }
    this.onFullOptionChoose.emit(option);
  }

  public onSelectedOptionClick(searchInput) {
    this.inputFocused = true;
    searchInput.classList.remove('display-none');
    setTimeout(function () {
      searchInput.click();
      searchInput.focus();
    });
  }

  private showDropdownList(): void {
    if (this._componentRef && this.filteredOptions && this.filteredOptions.length) {
      return;
    }
    this.toggled = true;

    // create a dropdown list component reference
    this._componentRef = this._componentFactoryResolver.resolveComponentFactory(DsDropdownListComponent).create(this._injector);
    this._componentRef.instance.host = this.toggle.nativeElement;
    this._componentRef.instance.template = this.template;
    this._componentRef.instance.hide = this.filteredOptions && !this.filteredOptions.length;
    this._componentRef.instance.position = this.dsDropdownPosition;
    this._componentRef.instance.align = this.dsDropdownAlign;
    this._componentRef.instance.maxHeight = this.dsDropdownListMaxHeight;
    this._componentRef.instance.width = this.dsDropdownListWidth;
    this._componentRef.instance.darkMode = this.darkMode;


    // attach component to the applicationRef so that it will be dirty checked.
    this._applicationRef.attachView(this._componentRef.hostView);

    // get DOM element from component
    const dropdownListElement: HTMLElement = (this._componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    document.body.appendChild(dropdownListElement);
    this._cdRef.markForCheck();
  }

  private hideDropdownList(target?: any): void {
    if (target && target.classList && target.classList.contains('mCSB_dragger')) {
      return;
    }
    const emptyValue = this.dsDropdownKeepInputValue ? this.optionFilter.value : '';

    this.dsDropdownSearch && this.optionFilter.setValue(this.selectedOption && this.selectedOption.label || emptyValue);
    this.toggled = false;
    this._componentRef && this._componentRef.destroy();
    this._componentRef = null;
    this._cdRef.markForCheck();
  }

}
